import { useEffect, useRef, useState } from "react";

/**
 * useInView – tiny IntersectionObserver hook.
 * Returns [ref, inView]. Attach ref to any element; inView flips to true once
 * the element scrolls into view. We unobserve after the first hit so the
 * animation only plays once (feels less jittery than re-triggering).
 */
const useInView = (options = { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setInView(true); // graceful fallback – show content
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.unobserve(node);
      }
    }, options);

    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, inView];
};

export default useInView;
